import { Component, OnInit, HostListener, inject, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ConfirmDialogComponent } from '../shared/confirm-dialog/confirm-dialog.component';
import { ContentService } from '../../services/content.service';
import { SdmUtilService } from '../../services/sdm-util.service';
import { SupportService } from '../../services/support.service';
import { UiHelperService } from '../../services/ui-helper.service';
import { Auth0LockService } from '../../services/auth0-lock.service';
import { PermissionService } from '../../services/permission.service';
import { SHARED_IMPORTS } from '../shared/shared-imports';

@Component({
	selector: 'top-menu-component',
	standalone: true,
	templateUrl: 'components/top-menu/top-menu.component.html',
	styleUrls: ['components/top-menu/top-menu.component.css'],
	styles: [`
		/* Angular Bootstrap replacement styles */
		.navbar-collapse.collapse {
			display: none !important;
		}
		
		.navbar-collapse.collapse.in {
			display: block !important;
		}
		
		.dropdown.open .dropdown-menu {
			display: block;
		}
		
		.dropdown-menu {
			display: none;
		}
		
		/* Responsive navbar toggle */
		@media (min-width: 768px) {
			.navbar-collapse.collapse {
				display: block !important;
				height: auto !important;
				padding-bottom: 0;
				overflow: visible !important;
			}
		}
	`],
	imports: [
		...SHARED_IMPORTS
	]

})
export class TopMenuComponent implements OnInit, OnDestroy {
	ready = false;
	private router = inject(Router);
	private dialog = inject(MatDialog);
	private content = inject(ContentService);
	private sdmUtil = inject(SdmUtilService);
	private supportService = inject(SupportService);
	private uiHelper = inject(UiHelperService);
	private auth0LockService = inject(Auth0LockService);
	private permissionService = inject(PermissionService);
	private authCheckInterval: any;

	constructor(
	) { }

	isAuthenticated = false;
	userEmail = '';
	menu: any[] = [];
	filteredMenu: any[] = [];
	settingsContent: string = '';
	overlayDropdown: any = null;
	permissionsLoaded = false;

	// Angular Bootstrap replacement properties
	navbarCollapsed = true;
	activeDropdown: number | null = null;
	userDropdownOpen = false;

	ngOnInit(): void {
		// Initialize menu immediately - don't wait for authentication
		this.initMenu();

		// Check authentication status immediately
		this.checkAuthenticationStatus();

		// Set up periodic check for authentication changes (to catch new logins)
		this.authCheckInterval = setInterval(() => {
			this.checkAuthenticationStatus();
		}, 1000); // Check every second for auth changes

		// Load permissions and filter menu when authenticated
		this.loadPermissionsAndFilterMenu();
	}

	ngOnDestroy(): void {
		// Clean up interval to prevent memory leaks
		if (this.authCheckInterval) {
			clearInterval(this.authCheckInterval);
		}
	}

	private checkAuthenticationStatus(): void {
		// Handle authentication - try direct localStorage check first (most reliable in hybrid mode)
		const token = localStorage.getItem('token') || sessionStorage.getItem('token');
		const emailStringified = localStorage.getItem('userEmail');

		const wasAuthenticated = this.isAuthenticated;
		const previousEmail = this.userEmail;

		if (token && emailStringified) {
			this.isAuthenticated = true;
			this.userEmail = emailStringified;

			// If authentication status changed, handle navigation and load permissions
			if (!wasAuthenticated) {
				// Show basic menu items immediately when authentication is detected
				this.showBasicMenuItems();
				
				// Force Angular Router to sync with browser URL
				if (window.location.pathname === '/Main' && this.router.url === '/') {
					this.router.navigateByUrl('/Main').then(() => {
					}).catch(err => {
					});
				}

				// Navigate to Main if we're on the root or empty path
				if (window.location.pathname === '/' || window.location.pathname === '') {
					this.router.navigate(['/Main']).then(() => {
					}).catch(err => {
					});
				}

				// Load permissions when authentication status changes (in background)
				this.loadPermissionsAndFilterMenu();
			}
		} else {
			this.isAuthenticated = false;
			this.userEmail = '';
			this.permissionsLoaded = false;
			this.filteredMenu = [];
			this.permissionService.clearCache();
		}

		// Also try the service-based approach as backup
		if (!this.isAuthenticated) {
			this.uiHelper.isAuthenticated.then(() => {
				this.isAuthenticated = true;
				if (!this.userEmail && emailStringified) {
					this.userEmail = emailStringified;
				}
				// Show basic menu items immediately, then load permissions
				this.showBasicMenuItems();
				this.loadPermissionsAndFilterMenu();
			}).catch(err => {
			});
		}
	}

	private async loadPermissionsAndFilterMenu(): Promise<void> {
		if (!this.isAuthenticated || this.permissionsLoaded) {
			return;
		}

		// Show basic menu items immediately (non-global items that don't require permissions)
		this.showBasicMenuItems();

		try {
			const permissionResponse = await this.permissionService.getCurrentPermissions();
			if (permissionResponse && permissionResponse.permissions) {
				this.permissionsLoaded = true;
				this.filterMenuByPermissions(permissionResponse.permissions);
			} else if (permissionResponse === null) {
				return; // Don't retry if user is not authenticated
			}
		} catch (error) {
			console.warn('Failed to load permissions on first attempt, will retry:', error);
			// Quick retry after a short delay
			setTimeout(async () => {
				try {
					const permissionResponse = await this.permissionService.getCurrentPermissions();
					if (permissionResponse && permissionResponse.permissions) {
						this.permissionsLoaded = true;
						this.filterMenuByPermissions(permissionResponse.permissions);
					} else if (permissionResponse === null) {
						console.log('TopMenuComponent: User not authenticated on retry, skipping permissions load');
						return; // Don't continue retrying if user is not authenticated
					}
				} catch (retryError) {
					console.error('Failed to load permissions after retry:', retryError);
					// Fallback: show all menu items without global permissions
					this.showBasicMenuItems();
				}
			}, 200); 
		}
	}

	private showBasicMenuItems(): void {
		// Show basic menu items that don't require global permissions immediately
		this.filteredMenu = this.menu.filter(menuItem => !this.isGlobalPermission(menuItem.sdmPermission));
	}

	private filterMenuByPermissions(permissions: Record<string, any>): void {
		this.filteredMenu = this.menu.filter(menuItem => {
			if (!menuItem.sdmPermission) {
				return true; // Show items without permission requirements
			}

			const hasPermission = this.checkPermission(menuItem.sdmPermission, permissions);
			
			// If main menu item has permission, also filter its submenu
			if (hasPermission && menuItem.submenu) {
				menuItem.submenu = menuItem.submenu.filter((submenuItem: any) => {
					if (!submenuItem.sdmPermission) {
						return true;
					}
					return this.checkPermission(submenuItem.sdmPermission, permissions);
				});
			}

			return hasPermission;
		});
	}

	private checkPermission(permission: string, permissions: Record<string, any>): boolean {
		if (!permission) return true;

		return (
			!!permissions[permission] ||
			(!!permissions['Tenant.*'] && !permission.includes('GlobalAdmin')) ||
			!!permissions['GlobalAdmin.*']
		);
	}

	private isGlobalPermission(permission: string): boolean {
		return permission && (permission.startsWith('Global.') || permission.includes('GlobalAdmin'));
	}

	initMenu(): void {
		this.menu = [
			{
				contentIdSuffix: 'Setup',
				submenu: [
					{ contentIdSuffix: 'EditLocations', url: '/LocationBaseDataEdit', sdmPermission: 'Location.EditLocations' },
					{ contentIdSuffix: 'AttachUnitsToLocations', url: '/LocationUnitAnchoring', sdmPermission: 'Location.UnitAnchoring' },
					{ contentIdSuffix: 'ManageUnitTemplates', url: '/ManageUnitTemplates', sdmPermission: 'User.ReadAll' },
					{ contentIdSuffix: 'TenantDetail', url: '/TenantDetail', sdmPermission: 'Tenant.Modify' }
				]
			},
			{
				contentIdSuffix: 'User',
				submenu: [
					{ contentIdSuffix: 'InviteUser', url: '/UserInvitationCreate', sdmPermission: 'User.Invite' },
					{ contentIdSuffix: 'UserList', url: '/UserList', sdmPermission: 'User.Read' },
					{ contentIdSuffix: 'UserActivityLog', url: '/UserActivityLog', sdmPermission: 'User.ReadActivityLog' }
				]
			},
			{
				contentIdSuffix: 'Export',
				submenu: [
					{ contentIdSuffix: 'ExportMeasurements', url: '/ExportMeasurement', sdmPermission: 'Measurement.Export' }
				]
			},
			{
				contentIdSuffix: 'Service',
				align: 'right',
				sdmPermission: 'Global.MoveUnitBetweenTenants', // Add permission to main menu item
				submenu: [
					{ contentIdSuffix: 'MoveUnitBetweenTenants', url: '/Global/MoveUnitBetweenTenants', sdmPermission: 'Global.MoveUnitBetweenTenants' },
					{ contentIdSuffix: 'GlobalAlarmList', url: '/Global/AlarmList', sdmPermission: 'Global.AlarmList' }
				]
			},
			{
				contentIdSuffix: 'Production',
				align: 'right',
				sdmPermission: 'Global.Main', // Add permission to main menu item
				submenu: [
					{ contentIdSuffix: 'Boostrap', url: '/cm/bootstrap', sdmPermission: 'Global.Main' },
					{ contentIdSuffix: 'GenerateSerialNumbers', url: '/cm/generateSerialNumber', sdmPermission: 'Global.Main' },
					{ contentIdSuffix: 'ScanclimeList', url: '/cm/scanclimeList', sdmPermission: 'Global.Main' },
					{ contentIdSuffix: 'ImportServiceLog', url: '/cm/importServiceLog', sdmPermission: 'Global.Main' }
				]
			}
		];
			
		this.addTitles(this.menu, 'TopMenu');
		this.settingsContent = this.content.getContent('TopMenu.UserPreferences');
		this.ready = true;
	}

	addTitles(nodes: any[], contentIdPrefix: string): void {
		nodes.forEach((node) => {
			node.title = ' ';
			const myContentId = `${contentIdPrefix}.${node.contentIdSuffix}`;
			this.content.getContentPromise(myContentId).then(value => {
				node.title = value;
			});
			if (node.submenu) {
				this.addTitles(node.submenu, myContentId);
			}
		});
	}

	toggleOverlayDropdown(panel: any): void {
		this.overlayDropdown = this.overlayDropdown === panel ? false : panel;
	}

	overlayDropdownClick(): void {
		this.overlayDropdown = false;
		this.closeNavbar();
	}

	logoClick(): void {
		this.closeNavbar();
		this.closeAllDropdowns();
		this.router.navigate(['/Main']);
	}

	// Angular Bootstrap replacement methods
	toggleNavbar(): void {
		this.navbarCollapsed = !this.navbarCollapsed;
		if (!this.navbarCollapsed) {
			// Close any open dropdowns when navbar opens
			this.closeAllDropdowns();
			this.overlayDropdown = 'navbar';
		} else {
			this.overlayDropdown = null;
		}
	}

	closeNavbar(): void {
		this.navbarCollapsed = true;
		this.closeAllDropdowns();
		this.overlayDropdown = null;
	}

	toggleDropdown(event: Event, index: number): void {
		event.preventDefault();
		if (this.activeDropdown === index) {
			this.activeDropdown = null;
		} else {
			this.activeDropdown = index;
		}
	}

	closeAllDropdowns(): void {
		this.activeDropdown = null;
		this.userDropdownOpen = false;
	}

	toggleUserDropdown(event: Event): void {
		event.preventDefault();
		this.userDropdownOpen = !this.userDropdownOpen;
		// Close main menu dropdowns when user dropdown opens
		if (this.userDropdownOpen) {
			this.activeDropdown = null;
		}
	}

	closeUserDropdown(): void {
		this.userDropdownOpen = false;
	}

	// TrackBy functions to prevent infinite loops in *ngFor
	trackByIndex(index: number, item: any): number {
		return index;
	}

	trackBySubmenuIndex(index: number, item: any): number {
		return index;
	}

	// Close dropdowns when clicking outside
	@HostListener('document:click', ['$event'])
	onDocumentClick(event: Event): void {
		const target = event.target as HTMLElement;

		// Don't close if clicking on dropdown toggles, dropdown content, or dialogs
		if (target.closest('.dropdown-toggle') || target.closest('.dropdown-menu') || target.closest('.mat-dialog-container')) {
			return;
		}

		// Close all dropdowns if clicking outside
		this.closeAllDropdowns();
	}

	reportError(): void {
		this.dialog.open(ConfirmDialogComponent, {
			data: {
				title: this.content.getContent("SupportService.ReportErrorTraceId", ''),
				text: this.content.getContent("SupportService.AddInformationOrYourSuggestionHere"),
				prompt: true,
				okText: this.content.getContent("SupportService.Send"),
				cancelText: this.content.getContent("SupportService.Cancel")
			}
		}).afterClosed().subscribe((input: string | false) => {
			if (typeof input === 'string' && input.trim()) {
				this.supportService.reportError('', input);
			}
		});
	}
}
